/**
 * @fileoverview Date/time helpers for the scheduling feature (Spec 04).
 *
 * The API speaks instructor-local "HH:MM" (24h) + "YYYY-MM-DD" strings and
 * absolute UTC `start_at` instants; occurrence identity is always
 * (slot_id, date). These helpers keep that vocabulary in one place.
 */

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const WEEKDAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Lesson formats an instructor can offer on the public booking page
 *  (mirrors the backend's BOOKING_LESSON_TYPES). */
export const LESSON_TYPE_LABELS = {
  studio: "Studio",
  virtual: "Virtual",
  home_visit: "Home visit",
};

/** The browser's IANA timezone — copied onto slots at creation. */
export function browserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** "YYYY-MM-DD" for a Date in local time. */
export function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Monday of the week containing `date` (local time). */
export function mondayOf(date) {
  const d = new Date(date);
  const shift = (d.getDay() + 6) % 7; // getDay: 0=Sun
  d.setDate(d.getDate() - shift);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** "16:05" → "4:05 PM" */
export function formatTime12h(timeLocal) {
  if (!timeLocal) return "";
  const [h, m] = timeLocal.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
}

/**
 * A zone's short name on a given day ("America/Los_Angeles" on 2026-10-06 →
 * "PDT"), falling back to the zone's own name where the browser has none.
 */
export function zoneAbbreviation(zone, ymd) {
  if (!zone) return "";
  try {
    const at = new Date(`${ymd || new Date().toISOString().slice(0, 10)}T12:00:00Z`);
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value || zone.replaceAll("_", " ");
  } catch {
    return zone.replaceAll("_", " ");
  }
}

/** Weekday name for an occurrence identity date ("2026-03-05" → "Thursday"). */
export function weekdayName(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return WEEKDAYS[(new Date(y, m - 1, d).getDay() + 6) % 7];
}

/**
 * Short calendar label for an occurrence identity date ("2026-10-23" →
 * "Fri, Oct 23"). Built from the date's own parts, so it is the same day in
 * every timezone (the identity date already is the lesson's local day), and
 * from fixed names rather than Intl, so iOS (mobile/src/lib/scheduleLabels.js)
 * prints the same label.
 */
export function shortDateLabel(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const weekday = WEEKDAYS_SHORT[(new Date(y, m - 1, d).getDay() + 6) % 7];
  return `${weekday}, ${MONTHS_SHORT[m - 1]} ${d}`;
}

/**
 * Relative day label for an occurrence identity date, in the viewer's local
 * time: "Today" / "Tomorrow" when it matches, otherwise the weekday name.
 */
export function relativeDayLabel(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((target - today) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return weekdayName(dateStr);
}

/** Short label like "Thu 4:00 PM · 30 min" for a slot doc. */
export function slotLabel(slot) {
  const day = slot.one_off_date ? slot.one_off_date : WEEKDAYS_SHORT[slot.weekday];
  return `${day} ${formatTime12h(slot.start_time_local)} · ${slot.duration_minutes} min`;
}

/** The stamp params for an occurrence, in whichever identity it has.
 *
 * Scheduler occurrences are (connection, appointment, revision); Hans-native
 * ones are (slot, date) — Spec 08 invariant #1. Everything that opens a write
 * form for a scheduled session goes through here, because a form opened
 * WITHOUT the stamp produces an unattributed lesson: program attribution is
 * inherited from the occurrence, so the organization never sees the note and
 * the session stays in the notes backlog forever.
 */
export function stampParams(occ, legacyKey = "slotId") {
  return occ.scheduler_appointment_id
    ? new URLSearchParams({
      connectionId: occ.scheduler_connection_id, appointmentId: occ.scheduler_appointment_id,
      revision: String(occ.scheduler_revision),
      ...(occ.scheduler_participants_revision ? { participantsRevision: occ.scheduler_participants_revision } : {}),
    })
    : new URLSearchParams(occ.slot_id ? { [legacyKey]: occ.slot_id, date: occ.date } : {});
}

/**
 * The typed-notes deep link. `slot` is the legacy key ManualLessonCompose reads.
 *
 * For a CLASS, `title` carries the class's own name so the compose page opens
 * with it already filled in: someone who tapped a class on their calendar has
 * said which lesson this is, and retyping its name is the page asking again.
 * Optional, and only a default — the field stays editable.
 *
 * Never for a solo lesson. There `display_name` is the STUDENT's name, and a
 * lesson called "Amara Osei" says nothing about the lesson — the title is
 * meant to describe what happened, and a bad default is worse than none.
 */
export function composeHrefForOccurrence(occ) {
  const params = stampParams(occ, "slot");
  params.set(occ.class_id ? "class" : "student", occ.class_id || occ.student_id);
  if (occ.class_id && occ.display_name) params.set("title", occ.display_name);
  return `/lesson/new?${params.toString()}`;
}

/** The /hans deep link for an occurrence, carrying the pre-link stamp. */
export function recordHrefForOccurrence(occ) {
  if (occ.recordable === false && !occ.attendance_reviewable) return "/settings";
  const params = stampParams(occ).toString();
  return occ.class_id ? `/hans/class/${occ.class_id}?${params}` : `/hans/${occ.student_id}?${params}`;
}
